/**
 * A small political map of a real-world theatre for the setup screens: water, the belligerents in
 * their colours, neutral countries hatched grey, capitals ringed. Drawn once per theatre from the
 * committed map data (`loadTheatreMap`, a few ms) onto a canvas.
 */
import {
  HEX_W,
  HexGrid,
  isWater,
  loadTheatreMap,
  NO_FACTION,
  THEATRES,
  type TheatreId,
} from '@friction/engine';
import { useEffect, useMemo, useRef } from 'react';
import { cssColor } from '../../game/format';

const WATER = '#6f8e9c';
const NEUTRAL = '#9a968c';
const PAPER = [0xe8, 0xdf, 0xc8];

/** Faction colour washed towards paper, as the staff map tints territory. */
function tint(color: number): string {
  const c = [(color >> 16) & 255, (color >> 8) & 255, color & 255];
  return `rgb(${c.map((v, i) => Math.round(PAPER[i] + (v - PAPER[i]) * 0.62)).join(',')})`;
}

/** The map fitted into `width` × `height` css px (keeping its aspect). */
export function TheatrePreview({
  id,
  width: maxWidth,
  height: maxHeight,
  label,
}: {
  id: TheatreId;
  width: number;
  height: number;
  label: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { map, grid } = useMemo(() => {
    const m = loadTheatreMap(id);
    return { map: m, grid: new HexGrid(m.width, m.height) };
  }, [id]);
  const scale = Math.min(maxWidth / grid.worldWidth, maxHeight / grid.worldHeight);
  const width = Math.round(grid.worldWidth * scale);
  const height = Math.round(grid.worldHeight * scale);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    const nations = THEATRES[id].nations;
    const fills = nations.map((n) => tint(n.color));
    const r = HEX_W / Math.sqrt(3) + 0.8;
    const hex = (h: number) => {
      const x = grid.cx(h);
      const y = grid.cy(h);
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 180) * (60 * i - 30);
        ctx.lineTo(x + r * Math.cos(a), y + r * Math.sin(a));
      }
      ctx.fill();
    };
    for (let h = 0; h < grid.count; h++) {
      const o = map.initialOwner[h];
      ctx.fillStyle = isWater(map.terrain[h]) ? WATER : o === NO_FACTION ? NEUTRAL : fills[o];
      hex(h);
    }
    // Hatch the neutral countries.
    ctx.save();
    ctx.beginPath();
    for (let h = 0; h < grid.count; h++) {
      if (isWater(map.terrain[h]) || map.initialOwner[h] !== NO_FACTION) continue;
      ctx.rect(grid.cx(h) - HEX_W / 2, grid.cy(h) - r, HEX_W, 2 * r);
    }
    ctx.clip();
    ctx.strokeStyle = 'rgba(40,38,34,0.35)';
    ctx.lineWidth = 1 / scale;
    const step = 7 / scale;
    for (let d = -grid.worldHeight; d < grid.worldWidth; d += step) {
      ctx.beginPath();
      ctx.moveTo(d, 0);
      ctx.lineTo(d + grid.worldHeight, grid.worldHeight);
      ctx.stroke();
    }
    ctx.restore();
    for (const c of map.cities) {
      if (c.size < 3 && c.capitalOf === NO_FACTION) continue;
      const x = grid.cx(c.hex);
      const y = grid.cy(c.hex);
      ctx.fillStyle = '#2b2620';
      ctx.beginPath();
      ctx.arc(x, y, (c.capitalOf !== NO_FACTION ? 3.2 : 2.2) / scale, 0, Math.PI * 2);
      ctx.fill();
      if (c.capitalOf !== NO_FACTION) {
        ctx.strokeStyle = cssColor(nations[c.capitalOf]?.color ?? 0);
        ctx.lineWidth = 1.6 / scale;
        ctx.beginPath();
        ctx.arc(x, y, 5.4 / scale, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }, [id, map, grid, scale, width, height]);

  return (
    <canvas
      ref={ref}
      style={{ width, height, display: 'block', borderRadius: 3 }}
      role="img"
      aria-label={label}
    />
  );
}
